import { parse } from "date-fns";
import { z } from "zod";

import { config } from "./config";

// Helper function to combine date and time
const combineDateAndTime = (date: Date, time: string): Date => {
	return parse(time, "HH:mm", date);
};

export const formSchema = z
	.object({
		name: z.string().trim().min(1, "This is a required field").max(280, "Name cannot exceed 280 characters"),
		startDate: z.date({ required_error: "This is a required field" }),
		endDate: z.date({ required_error: "This is a required field" }),
		startTime: z
			.string()
			.regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, "Invalid time format")
			.min(1, "This is a required field"),
		endTime: z
			.string()
			.regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, "Invalid time format")
			.min(1, "This is a required field"),
		description: z
			.string()
			.trim()
			.max(
				config.meetingDescriptionMaxLength,
				`Descriptions cannot exceed ${config.meetingDescriptionMaxLength} characters`
			)
			.optional()
			.or(z.literal("")),
		carryOver: z.boolean().default(false),
		addToCalendar: z.boolean().default(false),
	})
	.refine(
		(data) => {
			const startDateTime = combineDateAndTime(data.startDate, data.startTime);
			const endDateTime = combineDateAndTime(data.endDate, data.endTime);
			return endDateTime > startDateTime;
		},
		{
			message: "End date must occur after the start date",
			path: ["endDate"],
		}
	);

export type MeetingFormSchema = z.infer<typeof formSchema>;

export type FormErrors = Partial<Record<keyof MeetingFormSchema, string>>;
export type MeetingValidationErrors = Partial<Record<keyof MeetingFormSchema, { message: string }>>;

export const assignmentFormSchema = z.object({
	assignee: z
		.object({
			value: z.string(),
			label: z.string(),
		})
		.or(z.null()),
	dueDate: z
		.date()
		.refine((date) => date > new Date(), {
			message: "Due date must be in the future",
		})
		.or(z.null()),
});

export type AssignmentFormData = z.infer<typeof assignmentFormSchema>;

export type AssignmentValidationErrors = Partial<Record<keyof AssignmentFormData, { message: string }>>;

export const WorkspaceDetailsFiltersSchema = z.object({
	status: z.array(z.enum(config.statusOptions)).optional(),
	privateNotes: z.enum(config.privateNotesOptions).optional(),
	meetingDateStart: z.date().optional(),
	meetingDateEnd: z.date().optional(),
});

export type WorkspaceDetailsFilters = z.infer<typeof WorkspaceDetailsFiltersSchema>;
